/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, { Dispatch, SetStateAction, useState } from 'react';
import { capitalizeFirst } from '@/utils/capitalizeWord';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import EnhancedRadioSelect, {
  RadioOption,
} from '../combobox/enhanced-radio-select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface ViewBySelectorProps {
  viewBy: string;
  setViewBy: (value: string) => void;
  supportedViews: {
    key: string;
    label: string;
    hasSubviews: boolean;
    subviews?: any;
  }[];
  selectedQualityFilter: RadioOption | null;
  setSelectedQualityFilter: Dispatch<SetStateAction<RadioOption | null>>;
  onQualityFilterUpdate: (value: RadioOption | null) => void;
}

export const ViewBySelector: React.FC<ViewBySelectorProps> = ({
  viewBy,
  setViewBy,
  supportedViews,
  selectedQualityFilter,
  setSelectedQualityFilter,
  onQualityFilterUpdate,
}) => {
  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  const getDisplayValue = () => {
    const view = supportedViews.find((v) => v.key === viewBy);

    if (view?.hasSubviews && selectedQualityFilter) {
      return `${view?.label} > ${selectedQualityFilter.label}`;
    }
    return view?.label || viewBy;
  };

  return (
    <>
      <div className="flex h-7 items-center rounded-e rounded-s border-2 border-form-control-border-rest px-3 py-1">
        <Popover open={mainMenuOpen} onOpenChange={setMainMenuOpen}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button className="flex h-auto w-auto max-w-[300px] items-center gap-1.5 border-none bg-transparent p-0 text-[13px] leading-[18px] text-body-subtle hover:text-body-primary focus:outline-none">
                    <span className="block overflow-hidden truncate text-ellipsis whitespace-nowrap">
                      {getDisplayValue()}
                    </span>
                    <ChevronDown className="h-3 w-3 text-[#EBEFF1] opacity-100" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={20}
                className="h-[34px] border-none bg-[#07181E]"
              >
                <p>{getDisplayValue()}</p>
                <TooltipPrimitive.Arrow className="fill-[#07181E]" />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <PopoverContent
            className="w-auto min-w-[200px] overflow-visible rounded-none bg-white p-0 text-[#201F1E]"
            align="start"
            sideOffset={5}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {supportedViews.map((view) => {
              const isSelected = viewBy === view.key;

              const optionsArr = view?.subviews?.map(
                (subview: { name: string }) => ({
                  label: capitalizeFirst(subview?.name),
                  value: subview?.name,
                })
              );
              const isSubmenuOpen = activeSubmenu === view.key;

              return view?.hasSubviews ? (
                <div key={view.key} className="relative">
                  <div
                    className={`flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 text-[13px] hover:bg-[#F3F2F1] ${isSubmenuOpen ? 'bg-[#F3F2F1]' : ''} `}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveSubmenu(isSubmenuOpen ? null : view.key);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-4">
                        {isSelected && (
                          <Check className="h-4 w-4 text-[#257C9D]" />
                        )}
                      </span>
                      {view.label}
                    </div>

                    <ChevronRight className="h-4 w-4 text-[#8C9EA1]" />
                  </div>

                  {isSubmenuOpen && (
                    <EnhancedRadioSelect
                      options={optionsArr}
                      onUpdate={(value) => {
                        onQualityFilterUpdate(value);
                        if (value) {
                          setViewBy(view.key);
                        }
                        setActiveSubmenu(null);
                        setMainMenuOpen(false);
                      }}
                      isOpen={isSubmenuOpen}
                      setIsOpen={(open) =>
                        setActiveSubmenu(open ? view.key : null)
                      }
                      loading={false}
                      finalValue={selectedQualityFilter}
                      modalPopover={false}
                      side="left"
                      align="start"
                      sideOffset={0}
                      alignOffset={-32}
                    >
                      <div className="absolute left-full top-0" />
                    </EnhancedRadioSelect>
                  )}
                </div>
              ) : (
                <div
                  key={view.key}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-[13px] hover:bg-[#F3F2F1]"
                  onClick={() => {
                    setSelectedQualityFilter(null);
                    setViewBy(view.key);
                    setMainMenuOpen(false);
                  }}
                >
                  <span className="w-4">
                    {isSelected && <Check className="h-4 w-4 text-[#257C9D]" />}
                  </span>
                  {view.label}
                </div>
              );
            })}
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
};
